/**
 * Los campos de "que es lo que viaja".
 *
 * Un bloque que se usa en dos sitios --el buscador y el formulario de RFQ-- y
 * que en los dos hace lo mismo: un desplegable con cuatro opciones y, debajo,
 * SOLO los campos de la que este elegida.
 *
 * ===========================================================================
 * SE ENSENAN LOS CAMPOS DE UNA NATURALEZA, NO LOS DE LAS CUATRO
 *
 * La alternativa --pintar los siete campos y deshabilitar los que no aplican--
 * llena la pantalla de controles apagados y obliga a leerlos para descubrir que
 * no hay que rellenarlos. Con GENERAL, que es el noventa por ciento de los
 * embarques, no aparece ninguno: la declaracion es una linea y no un
 * formulario.
 *
 * ===========================================================================
 * LO QUE SE ESCRIBIO NO SE BORRA AL CAMBIAR
 *
 * El estado conserva los siete campos aunque solo se pinten los de la
 * naturaleza elegida: volver de PELIGROSA a REFRIGERADA no cuesta teclear la
 * temperatura otra vez. Lo que NO viaja en la peticion es lo que no aplica
 * [carga.ts], que es donde importa.
 */

import { el } from '../dom';
import type { Traductor } from '../i18n';
import { NATURALEZAS, type FormularioCarga, type Naturaleza } from '../carga';

export interface OpcionesCarga {
  readonly t: Traductor;
  readonly valores: FormularioCarga;
  /** Prefijo de los `id` de los controles. Este bloque se pinta dos veces en la
   *  misma pagina --buscador y RFQ-- y dos `id` iguales rompen los `<label for>`
   *  de los dos. */
  readonly prefijo: string;
  readonly faltan: readonly string[];
  readonly onCambio: (v: FormularioCarga) => void;
}

function entrada(
  id: string,
  etiqueta: string,
  valor: string,
  falta: boolean,
  onCambio: (v: string) => void,
  extra: Record<string, string> = {},
  ayuda?: string,
): HTMLElement {
  const control = el('input', {
    clase: 'control',
    attrs: { id, type: 'text', value: valor, ...extra },
  });
  control.addEventListener('input', () => {
    onCambio(control.value);
  });

  return el('div', { clase: `campo ${falta ? 'campo-falta' : ''}` }, [
    el('label', { clase: 'etiqueta', texto: etiqueta, attrs: { for: id } }),
    control,
    ayuda === undefined ? null : el('p', { clase: 'ayuda tenue', texto: ayuda }),
  ]);
}

export function pintarCarga(o: OpcionesCarga): HTMLElement {
  const { t, valores: v, prefijo: p } = o;
  const falta = (campo: string): boolean => o.faltan.includes(campo);
  const cambiar = (parcial: Partial<FormularioCarga>): void => {
    o.onCambio({ ...v, ...parcial });
  };

  const selector = el('select', { clase: 'control', attrs: { id: `${p}-naturaleza` } });
  for (const n of NATURALEZAS) {
    selector.appendChild(
      el('option', { texto: t(`carga.naturaleza.${n}`), attrs: { value: n } }),
    );
  }
  selector.value = v.naturaleza;
  selector.addEventListener('change', () => {
    // Se cambia SOLO la naturaleza: los demas campos se conservan. Ver la
    // cabecera. Quien pinta repinta, porque los campos de debajo cambian.
    cambiar({ naturaleza: selector.value as Naturaleza });
  });

  return el('div', { clase: 'bloque-carga' }, [
    el('div', { clase: 'campo' }, [
      el('label', {
        clase: 'etiqueta',
        texto: t('carga.titulo'),
        attrs: { for: `${p}-naturaleza` },
      }),
      selector,
      el('p', { clase: 'ayuda tenue', texto: t(`carga.ayuda.${v.naturaleza}`) }),
    ]),

    v.naturaleza === 'REFRIGERADA'
      ? entrada(
          `${p}-temperatura`,
          t('carga.campo.temperatura'),
          v.temperaturaC,
          falta('temperatura_c'),
          (x) => {
            cambiar({ temperaturaC: x });
          },
          { inputmode: 'decimal', placeholder: '-18' },
          t('carga.ayudaCampo.temperatura'),
        )
      : null,

    v.naturaleza === 'PELIGROSA'
      ? el('div', { clase: 'carga-fila' }, [
          entrada(
            `${p}-imo-clase`,
            t('carga.campo.imoClase'),
            v.imoClase,
            falta('imo_clase'),
            (x) => {
              cambiar({ imoClase: x });
            },
            { placeholder: '3', spellcheck: 'false' },
            t('carga.ayudaCampo.imoClase'),
          ),
          entrada(
            `${p}-imo-un`,
            t('carga.campo.imoUn'),
            v.imoUn,
            falta('imo_un'),
            (x) => {
              cambiar({ imoUn: x });
            },
            { placeholder: '1203', inputmode: 'numeric', spellcheck: 'false' },
            t('carga.ayudaCampo.imoUn'),
          ),
        ])
      : null,

    v.naturaleza === 'SOBREDIMENSIONADA'
      ? el('div', { clase: 'carga-fila' }, [
          entrada(
            `${p}-largo`,
            t('carga.campo.largo'),
            v.largoCm,
            falta('dim_largo_cm'),
            (x) => {
              cambiar({ largoCm: x });
            },
            { inputmode: 'numeric' },
          ),
          entrada(
            `${p}-ancho`,
            t('carga.campo.ancho'),
            v.anchoCm,
            falta('dim_ancho_cm'),
            (x) => {
              cambiar({ anchoCm: x });
            },
            { inputmode: 'numeric' },
          ),
          entrada(
            `${p}-alto`,
            t('carga.campo.alto'),
            v.altoCm,
            falta('dim_alto_cm'),
            (x) => {
              cambiar({ altoCm: x });
            },
            { inputmode: 'numeric' },
            t('carga.ayudaCampo.dimensiones'),
          ),
        ])
      : null,

    // La nota se ofrece siempre, tambien con GENERAL: es donde cabe lo que las
    // cuatro naturalezas no cubren, y viaja al correo del proveedor tal cual.
    entrada(
      `${p}-nota`,
      t('carga.campo.nota'),
      v.nota,
      false,
      (x) => {
        cambiar({ nota: x });
      },
      { maxlength: '500' },
    ),
  ]);
}

/**
 * La declaracion en una linea, para pintarla donde no se edita: la cotizacion
 * emitida, el resumen del cotizador.
 *
 * Devuelve `null` con GENERAL y sin nota: no hay nada que decir, y una linea
 * que dice "carga general" en cada cotizacion se deja de leer --y con ella se
 * deja de leer la que dice PELIGROSA--.
 */
export function resumirCarga(
  carga: Record<string, unknown> | undefined,
  t: Traductor,
): string | null {
  if (carga === undefined) return null;

  /** Un campo del mapa, si vino y es algo que se puede escribir. Se comprueba
   *  el TIPO en vez de pasar cualquier cosa por `String()`: la respuesta es un
   *  mapa suelto --los campos que no aplican no vienen [api.ts]-- y un objeto
   *  inesperado saldria como "[object Object]" dentro del documento. */
  const texto = (clave: string): string | null => {
    const v = carga[clave];
    if (typeof v === 'string') return v;
    if (typeof v === 'number') return String(v);
    return null;
  };

  const naturaleza = texto('naturaleza') ?? 'GENERAL';
  const partes: string[] = [];

  if (naturaleza !== 'GENERAL') partes.push(t(`carga.naturaleza.${naturaleza}`));

  const temp = texto('temperatura_c');
  if (temp !== null) partes.push(`${temp} °C`);

  const clase = texto('imo_clase');
  const un = texto('imo_un');
  if (clase !== null && un !== null) partes.push(`IMO ${clase} · UN${un}`);

  const largo = texto('dim_largo_cm');
  const ancho = texto('dim_ancho_cm');
  const alto = texto('dim_alto_cm');
  if (largo !== null && ancho !== null && alto !== null) {
    partes.push(`${largo} × ${ancho} × ${alto} cm`);
  }

  const nota = texto('naturaleza_nota');
  if (nota !== null) partes.push(nota);

  return partes.length === 0 ? null : partes.join(' · ');
}
